import { VEHICLE_CLASSES } from '@fleetcore/shared'
import { liveConnectivity, type LiveConnectivity } from '@fleetcore/shared/telemetry'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Search, SearchX } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom'

import { EmptyState } from '../../components/empty-state'
import { PageHeader } from '../../components/page-header'
import { Badge } from '../../components/ui/badge'
import { Card, CardContent } from '../../components/ui/card'
import { Skeleton } from '../../components/ui/skeleton'
import { currentLocale } from '../../i18n'
import { errorMessageKey } from '../../lib/api-error'
import { formatAge, useNow } from '../map/relative-time'
import { fetchVehicles } from './api'
import type { VehicleStatus, VehicleView } from './api-types'
import { NoVehiclesState } from './empty-states'
import { vehicleDetailPath } from './routes'

/**
 * The fleet register: every vehicle and engin, its box, and whether we are hearing it.
 *
 * The last column is the one this page exists for. "Is the tracker in that truck still
 * talking to us" is the first question after an install and the first question after
 * a complaint, and until now the only way to answer it was to find the vehicle on the
 * map — which a silent vehicle is, by definition, the hardest thing to find on.
 *
 * Three answers there, never collapsed into one:
 *   - no box at all            → nothing to hear; an install job, not an outage;
 *   - a box that never reported → wrong IMEI, wrong server address, or no SIM data;
 *   - a box that went silent   → the vehicle is somewhere without coverage, or the box
 *                                lost power — and the age says since when.
 * Online/stale/offline is the same shared rule the map and the detail page use, so a
 * vehicle cannot be "en ligne" here and "hors ligne" one click away.
 */

/** The API's own cap. Asking for more is a 400, which is how Trajets broke (#1). */
const PAGE_SIZE = 200

const QUERY_KEYS = { q: 'q', vehicleClass: 'type', status: 'statut' } as const

const RECORD_STATUSES: readonly VehicleStatus[] = [
  'active',
  'inactive',
  'maintenance',
  'sold',
  'archived',
]

const CONNECTIVITY_TONE: Record<LiveConnectivity, 'success' | 'warning' | 'neutral'> = {
  online: 'success',
  stale: 'warning',
  offline: 'neutral',
}

const HEAD = 'px-2 py-1.5 text-start text-xs font-medium uppercase tracking-wide text-foreground-subtle' // prettier-ignore
const CELL = 'px-2 py-2.5 align-top text-sm'

export function VehiclesPage() {
  const { t } = useTranslation()
  const [params, setParams] = useSearchParams()

  const q = params.get(QUERY_KEYS.q) ?? ''
  const vehicleClass = asOneOf(params.get(QUERY_KEYS.vehicleClass), VEHICLE_CLASSES)
  const status = asOneOf(params.get(QUERY_KEYS.status), RECORD_STATUSES)
  const filtered = q.trim().length > 0 || vehicleClass !== undefined || status !== undefined

  const setParam = (key: string, value: string) => {
    const updated = new URLSearchParams(params)
    if (value === '') updated.delete(key)
    else updated.set(key, value)
    setParams(updated, { replace: true })
  }

  const vehicles = useQuery({
    queryKey: ['vehicles', 'list', { q: q.trim(), vehicleClass, status }],
    queryFn: () =>
      fetchVehicles({
        pageSize: PAGE_SIZE,
        ...(q.trim().length > 0 ? { q: q.trim() } : {}),
        ...(vehicleClass !== undefined ? { vehicleClass } : {}),
        ...(status !== undefined ? { status } : {}),
      }),
    // Typing in the search box must narrow the table, not blank it to a skeleton on
    // every keystroke while the next page is in flight.
    placeholderData: keepPreviousData,
    // The "last position" column ages on its own; the list itself changes rarely.
    staleTime: 30_000,
  })

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <PageHeader title={t('vehicles:title')} description={t('vehicles:subtitle')} />

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 pt-4">
          <div className="flex w-full flex-col gap-1 sm:w-auto">
            <label htmlFor="vehicles-search" className="text-xs font-medium text-foreground-muted">
              {t('vehicles:list.search')}
            </label>
            <div className="relative">
              <Search
                aria-hidden
                className="pointer-events-none absolute start-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-foreground-subtle"
              />
              <input
                id="vehicles-search"
                type="search"
                value={q}
                placeholder={t('vehicles:list.searchPlaceholder')}
                onChange={(event) => setParam(QUERY_KEYS.q, event.target.value)}
                className="h-9 w-full rounded-md border border-border bg-surface ps-7 pe-2 text-sm text-foreground sm:w-64"
              />
            </div>
          </div>

          <div className="flex min-w-0 flex-1 flex-col gap-1 sm:flex-none">
            <label htmlFor="vehicles-class" className="text-xs font-medium text-foreground-muted">
              {t('vehicles:columns.class')}
            </label>
            <select
              id="vehicles-class"
              value={vehicleClass ?? ''}
              onChange={(event) => setParam(QUERY_KEYS.vehicleClass, event.target.value)}
              className="h-9 w-full rounded-md border border-border bg-surface px-2 text-sm text-foreground sm:w-40"
            >
              <option value="">{t('vehicles:list.allClasses')}</option>
              {VEHICLE_CLASSES.map((value) => (
                <option key={value} value={value}>
                  {t(`vehicles:class.${value}`)}
                </option>
              ))}
            </select>
          </div>

          <div className="flex min-w-0 flex-1 flex-col gap-1 sm:flex-none">
            <label htmlFor="vehicles-status" className="text-xs font-medium text-foreground-muted">
              {t('vehicles:columns.status')}
            </label>
            <select
              id="vehicles-status"
              value={status ?? ''}
              onChange={(event) => setParam(QUERY_KEYS.status, event.target.value)}
              className="h-9 w-full rounded-md border border-border bg-surface px-2 text-sm text-foreground sm:w-40"
            >
              <option value="">{t('vehicles:list.allStatuses')}</option>
              {RECORD_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {t(`vehicles:recordStatus.${value}`)}
                </option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {vehicles.isError ? (
        <EmptyState
          title={t('vehicles:list.error')}
          description={t(errorMessageKey(vehicles.error))}
        />
      ) : vehicles.data === undefined ? (
        <Skeleton className="h-64 w-full" />
      ) : vehicles.data.items.length === 0 ? (
        // Two different empties. With no filter, the tenant has no vehicles and the
        // useful thing to say is how to add one; with a filter, the search missed and
        // the onboarding copy would be wrong — the vehicles exist.
        filtered ? (
          <EmptyState
            icon={SearchX}
            title={t('vehicles:list.noMatch.title')}
            description={t('vehicles:list.noMatch.description')}
          />
        ) : (
          <NoVehiclesState />
        )
      ) : (
        <VehicleTable items={vehicles.data.items} total={vehicles.data.total} />
      )}
    </div>
  )
}

function VehicleTable({ items, total }: { items: readonly VehicleView[]; total: number }) {
  const { t } = useTranslation()
  // Re-render every 15 s so "il y a 2 min" keeps counting, and a vehicle crosses from
  // online to offline on screen without a reload.
  const nowMs = useNow(15_000)

  return (
    <Card>
      <CardContent className="pt-4">
        {/*
          Two layouts, one shown. Below md a six-column table only fits by scrolling
          sideways inside the card — and the column that scrolls off first is the one
          this page exists for. On a phone each vehicle is a card instead, with whether
          we are hearing it on the same line as its registration.
        */}
        <ul aria-label={t('vehicles:title')} className="divide-y divide-border md:hidden">
          {items.map((vehicle) => (
            <VehicleCard key={vehicle.id} vehicle={vehicle} nowMs={nowMs} />
          ))}
        </ul>
        <div className="hidden overflow-x-auto md:block">
          <table className="w-full min-w-[52rem] border-collapse">
            <caption className="sr-only">{t('vehicles:title')}</caption>
            <thead>
              <tr className="border-b border-border">
                <th scope="col" className={HEAD}>
                  {t('vehicles:columns.registration')}
                </th>
                <th scope="col" className={HEAD}>
                  {t('vehicles:columns.class')}
                </th>
                <th scope="col" className={HEAD}>
                  {t('vehicles:columns.makeModel')}
                </th>
                <th scope="col" className={HEAD}>
                  {t('vehicles:columns.status')}
                </th>
                <th scope="col" className={HEAD}>
                  {t('vehicles:columns.device')}
                </th>
                <th scope="col" className={HEAD}>
                  {t('vehicles:columns.lastSeen')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {items.map((vehicle) => (
                <VehicleRow key={vehicle.id} vehicle={vehicle} nowMs={nowMs} />
              ))}
            </tbody>
          </table>
        </div>

        {total > items.length ? (
          // The API stops at 200. Saying so beats a list that quietly looks complete.
          <p className="mt-3 text-xs text-foreground-muted">
            {t('vehicles:list.truncated', { shown: items.length, n: total })}
          </p>
        ) : null}
      </CardContent>
    </Card>
  )
}

function VehicleRow({ vehicle, nowMs }: { vehicle: VehicleView; nowMs: number }) {
  const { t } = useTranslation()
  const makeModel = [vehicle.make, vehicle.model].filter(Boolean).join(' ')

  return (
    <tr>
      <td className={CELL}>
        <Link
          to={vehicleDetailPath(vehicle.id)}
          className="font-medium text-foreground underline-offset-2 hover:underline"
        >
          {vehicle.registration}
        </Link>
      </td>
      <td className={CELL}>
        <Badge tone="accent">{t(`vehicles:class.${vehicle.vehicleClass}`)}</Badge>
        {vehicle.machineSubtype !== null ? (
          <span className="ms-1.5 text-xs text-foreground-muted">
            {t(`vehicles:machineSubtype.${vehicle.machineSubtype}`)}
          </span>
        ) : null}
      </td>
      <td className={`${CELL} text-foreground-muted`}>
        {makeModel.length > 0 ? makeModel : '—'}
        {vehicle.year !== null ? ` · ${vehicle.year}` : ''}
      </td>
      <td className={CELL}>
        <Badge tone={vehicle.status === 'active' ? 'success' : 'warning'}>
          {t(`vehicles:recordStatus.${vehicle.status}`)}
        </Badge>
      </td>
      <td className={CELL}>
        {vehicle.device === null ? (
          <Badge tone="warning">{t('vehicles:list.noDevice')}</Badge>
        ) : (
          <div className="flex flex-col gap-0.5">
            <span className="font-mono text-xs text-foreground">{vehicle.device.imei}</span>
            <span className="text-xs text-foreground-subtle">
              {t(`vehicles:installType.${vehicle.installType}`)}
            </span>
          </div>
        )}
      </td>
      <td className={CELL}>
        <LastSeen vehicle={vehicle} nowMs={nowMs} />
      </td>
    </tr>
  )
}

function VehicleCard({ vehicle, nowMs }: { vehicle: VehicleView; nowMs: number }) {
  const { t } = useTranslation()
  const makeModel = [vehicle.make, vehicle.model].filter(Boolean).join(' ')

  return (
    <li className="py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Link
            to={vehicleDetailPath(vehicle.id)}
            className="font-medium text-foreground underline-offset-2 hover:underline"
          >
            {vehicle.registration}
          </Link>
          <p className="truncate text-xs text-foreground-muted">
            {makeModel.length > 0 ? makeModel : '—'}
            {vehicle.year !== null ? ` · ${vehicle.year}` : ''}
          </p>
        </div>
        <LastSeen vehicle={vehicle} nowMs={nowMs} align="end" />
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <Badge tone="accent">{t(`vehicles:class.${vehicle.vehicleClass}`)}</Badge>
        <Badge tone={vehicle.status === 'active' ? 'success' : 'warning'}>
          {t(`vehicles:recordStatus.${vehicle.status}`)}
        </Badge>
        {vehicle.device === null ? (
          <Badge tone="warning">{t('vehicles:list.noDevice')}</Badge>
        ) : (
          <span className="font-mono text-xs text-foreground-subtle">{vehicle.device.imei}</span>
        )}
      </div>
    </li>
  )
}

function LastSeen({
  vehicle,
  nowMs,
  align = 'start',
}: {
  vehicle: VehicleView
  nowMs: number
  align?: 'start' | 'end'
}) {
  const { t } = useTranslation()
  // No box: there is nothing to hear, and "hors ligne" would send someone looking for
  // a fault in a tracker that was never fitted.
  if (vehicle.device === null) return <span className="text-foreground-subtle">—</span>

  const seenMs = vehicle.device.lastSeenAt === null ? NaN : Date.parse(vehicle.device.lastSeenAt)
  // Never reported is its own answer, not the oldest kind of offline: it points at the
  // install — the IMEI, the server address, the SIM — rather than at the vehicle.
  if (Number.isNaN(seenMs)) return <Badge tone="warning">{t('vehicles:list.never')}</Badge>

  const connectivity = liveConnectivity(seenMs, nowMs)
  return (
    <div
      className={`flex shrink-0 flex-col gap-0.5 ${align === 'end' ? 'items-end' : 'items-start'}`}
    >
      <Badge tone={CONNECTIVITY_TONE[connectivity]}>
        {t(`vehicles:connectivity.${connectivity}`)}
      </Badge>
      <span className="text-xs text-foreground-subtle">
        {formatAge(currentLocale(), Math.max(0, (nowMs - seenMs) / 1000))}
      </span>
    </div>
  )
}

/** A URL parameter is user input: anything outside the vocabulary means "no filter". */
function asOneOf<T extends string>(value: string | null, allowed: readonly T[]): T | undefined {
  return value !== null && (allowed as readonly string[]).includes(value) ? (value as T) : undefined
}
